/**
 * Quick Command Processor
 * Handles common commands instantly without AI processing
 */

import { roomDisplayName } from '@shared-lib/rooms/roomDisplayName';

// Exact match commands (case-insensitive)
const EXACT_COMMANDS = {
  // Navigation
  home: { type: 'navigate', path: '/' },
  projects: { type: 'navigate', path: '/' },
  back: { type: 'navigate', path: -1 },
  'go back': { type: 'navigate', path: -1 },
  forward: { type: 'navigate', path: 1 },
  'go forward': { type: 'navigate', path: 1 },

  // Actions
  logout: { type: 'action', action: 'logout' },
  'sign out': { type: 'action', action: 'logout' },
  fullscreen: { type: 'action', action: 'fullscreen' },
  'toggle fullscreen': { type: 'action', action: 'fullscreen' },
  'exit fullscreen': { type: 'action', action: 'fullscreen' },
  'clear chat': { type: 'action', action: 'clearChat' },
  'delete chat': { type: 'action', action: 'clearChat' },
  'new chat': { type: 'action', action: 'clearChat' },

  // Help
  help: { type: 'help' },
  '?': { type: 'help' },
  commands: { type: 'help' },
  'what can you do': { type: 'help' },

  // Debug/Info
  context: { type: 'showContext' },
  'show context': { type: 'showContext' },
  'debug context': { type: 'showContext' },

  // Renderings - these will be handled by pattern commands with context check
  // But adding here for autocomplete suggestions
  'show renderings': { type: 'action', action: 'openRenderings' },
  'list renderings': { type: 'action', action: 'openRenderings' },
};

// Pattern-based commands
const PATTERN_COMMANDS = [
  // Export patterns
  {
    pattern: /^export(?:\s+(?:this|as))?\s+pdf$/i,
    handler: { type: 'action', action: 'exportPDF' },
  },
  {
    pattern: /^export(?:\s+(?:this|as))?\s+ifc$/i,
    handler: { type: 'action', action: 'exportIFC' },
  },
  {
    pattern: /^export(?:\s+(?:this|as))?\s+dxf$/i,
    handler: { type: 'action', action: 'exportDXF' },
  },

  // View mode navigation patterns
  {
    pattern: /^(?:switch to|show|view|go to)?\s*orbit(?:\s+(?:mode|view))?$/i,
    handler: (matches, context) => {
      // Check if we're in a structure
      if (!context?.location?.structureId) {
        return {
          type: 'message',
          message: 'Please open a structure first to switch view modes.',
        };
      }
      return {
        type: 'action',
        action: 'switchViewMode',
        viewMode: 'orbit',
        message: 'Switching to Orbit view...',
      };
    },
  },
  {
    pattern:
      /^(?:switch to|show|view|go to)?\s*first\s*person(?:\s+(?:mode|view))?$/i,
    handler: (matches, context) => {
      // Check if we're in a structure
      if (!context?.location?.structureId) {
        return {
          type: 'message',
          message: 'Please open a structure first to switch view modes.',
        };
      }
      return {
        type: 'action',
        action: 'switchViewMode',
        viewMode: 'firstPerson',
        message: 'Switching to First Person view...',
      };
    },
  },
  {
    pattern:
      /^(?:switch to|show|view|go to)?\s*(?:floor\s*plan|floorplan)(?:\s+(?:mode|view))?$/i,
    handler: (matches, context) => {
      // Check if we're in a structure
      if (!context?.location?.structureId) {
        return {
          type: 'message',
          message: 'Please open a structure first to switch view modes.',
        };
      }
      return {
        type: 'action',
        action: 'switchViewMode',
        viewMode: 'floorplan',
        message: 'Switching to Floor Plan view...',
      };
    },
  },
  {
    pattern:
      /^(?:switch to|show|view|go to)?\s*elevations?(?:\s+(?:mode|view))?$/i,
    handler: (matches, context) => {
      // Check if we're in a structure
      if (!context?.location?.structureId) {
        return {
          type: 'message',
          message: 'Please open a structure first to switch view modes.',
        };
      }
      return {
        type: 'action',
        action: 'switchViewMode',
        viewMode: 'elevations',
        message: 'Switching to Elevations view...',
      };
    },
  },

  // Simple "go to {name}" pattern - prioritize structures in current project, then projects
  {
    pattern: /^(?:go to|open|show)\s+(.+)$/i,
    handler: (matches, context) => {
      const targetName = matches[1].trim();

      // Skip if it matches other patterns like "go to bathroom"
      const roomTypes = [
        'bathroom',
        'kitchen',
        'bedroom',
        'living',
        'dining',
        'office',
      ];
      if (roomTypes.some((type) => targetName.toLowerCase().includes(type))) {
        return null; // Let other patterns handle this
      }

      // Skip if it's a page navigation like "go back"
      if (
        ['back', 'forward', 'home', 'projects'].includes(
          targetName.toLowerCase()
        )
      ) {
        return null; // Let exact commands handle this
      }

      // First, check if we're viewing a project and have structures cached
      const currentProjectId = context?.location?.projectId;
      const structures = context?.cache?.structures || [];

      if (currentProjectId && structures.length > 0) {
        // Try to match against structure names first
        let structure = structures.find(
          (s) => s.name?.toLowerCase() === targetName.toLowerCase()
        );

        // If no exact match, try partial match
        if (!structure) {
          structure = structures.find((s) =>
            s.name?.toLowerCase().includes(targetName.toLowerCase())
          );
        }

        if (structure) {
          // Found a structure in the current project! Navigate to it
          const params = new URLSearchParams();
          params.append('projectId', currentProjectId);
          params.append('navigationMode', 'floorplan');
          const queryString = params.toString();
          return {
            type: 'navigate',
            path: `/structure/${structure.id}?${queryString}`,
            message: `Opening structure: ${structure.name}`,
          };
        }
      }

      // If not found in structures, try to match against project names
      const projects = context?.cache?.projects || [];

      if (projects.length > 0) {
        // Try exact match first (case-insensitive)
        let project = projects.find(
          (p) => p.name.toLowerCase() === targetName.toLowerCase()
        );

        // If no exact match, try partial match
        if (!project) {
          project = projects.find((p) =>
            p.name.toLowerCase().includes(targetName.toLowerCase())
          );
        }

        if (project) {
          // We found it locally! Navigate immediately
          return {
            type: 'navigate',
            path: `/project/${project.id}`,
            message: `Opening project: ${project.name}`,
          };
        }
      }

      // Didn't match anything, let AI handle it
      return null;
    },
  },

  // Go to project pattern - explicit project keyword
  {
    pattern: /^(?:go to|open|show)\s+project\s+(.+)$/i,
    handler: (matches, context) => {
      const projectName = matches[1].trim();

      // Use clean context structure
      const projects = context?.cache?.projects || [];

      // If we have local project data, try to match immediately
      if (projects.length > 0) {
        const project = projects.find(
          (p) => p.name.toLowerCase() === projectName.toLowerCase()
        );

        if (project) {
          // We found it locally! Navigate immediately
          return {
            type: 'navigate',
            path: `/project/${project.id}`,
            message: `Opening project: ${project.name}`,
          };
        }
      }

      // Need AI to search
      return {
        type: 'navigateProject',
        projectName,
        needsAI: true,
      };
    },
  },

  // Go to room pattern - check if we can handle locally
  {
    pattern: /^(?:go to|open|show)\s+(?:the\s+)?(.+?)\s*(?:room)?$/i,
    handler: (matches, context) => {
      const roomTypes = [
        'bathroom',
        'kitchen',
        'bedroom',
        'living',
        'dining',
        'office',
        'hallway',
        'closet',
        'laundry',
        'utility',
      ];
      const searchTerm = matches[1].toLowerCase().trim();

      if (roomTypes.some((type) => searchTerm.includes(type))) {
        // Use clean context structure
        const structureId = context?.location?.structureId;
        const rooms = context?.cache?.rooms || [];

        // If we're in a structure and have room data, try to match immediately
        if (structureId && rooms.length > 0) {
          // Try to find a room that matches by name, label, type, category, or section labels
          let room = rooms.find((r) => {
            // Check room name
            if (r.name?.toLowerCase().includes(searchTerm)) return true;

            // Check room label
            if (r.label?.toLowerCase().includes(searchTerm)) return true;

            // Check room type
            if (r.type?.toLowerCase().includes(searchTerm)) return true;

            // Check room category
            if (r.category?.toLowerCase().includes(searchTerm)) return true;

            // Check section labels
            if (
              r.sectionLabels?.some((label) =>
                label.toLowerCase().includes(searchTerm)
              )
            )
              return true;

            // Check searchableText if available
            if (r.searchableText?.includes(searchTerm)) return true;

            return false;
          });

          if (room) {
            // Found it locally!
            const projectId = context?.location?.projectId;
            const params = new URLSearchParams();
            if (projectId) params.append('projectId', projectId);
            params.append('roomId', room.id);
            const queryString = params.toString();

            return {
              type: 'navigate',
              path: `/structure/${structureId}?${queryString}`,
              message: `Going to ${roomDisplayName(room.name || room.label || room.type) ?? 'room'}`,
            };
          }
        }

        // Need AI to search across structures
        return {
          type: 'navigateRoom',
          roomName: matches[1].trim(),
          needsAI: true,
        };
      }
      return null;
    },
  },

  // Show checkpoints pattern
  {
    pattern: /^(?:show|open|view|list)\s+checkpoints?$/i,
    handler: (matches, context) => {
      // Use clean context structure
      const structureId = context?.location?.structureId;

      if (structureId) {
        return {
          type: 'action',
          action: 'openCheckpoints',
          structureId: structureId,
        };
      } else {
        return {
          type: 'action',
          action: 'noStructureForCheckpoints',
          message: 'Please open a structure first to view its checkpoints.',
        };
      }
    },
  },

  // Show renderings/renders pattern
  {
    pattern: /^(?:show|open|view|list)\s+(?:ai\s+)?(?:renderings?|renders?)$/i,
    handler: (matches, context) => {
      // Use clean context structure
      const structureId = context?.location?.structureId;

      if (structureId) {
        return {
          type: 'action',
          action: 'openRenderings',
          structureId: structureId,
        };
      } else {
        return {
          type: 'action',
          action: 'noStructureForRenderings',
          message: 'Please open a structure first to view its renderings.',
        };
      }
    },
  },
];

/**
 * Process a quick command
 * @param {string} input - User input
 * @param {Object} context - Current context with location, cache, navigation, etc.
 * @returns {Object|null} Command result or null if not a quick command
 */
export function processQuickCommand(input, context = {}) {
  if (!input) return null;

  const trimmedInput = input.trim().toLowerCase();

  // Check exact commands first
  if (EXACT_COMMANDS[trimmedInput]) {
    return {
      isQuickCommand: true,
      ...EXACT_COMMANDS[trimmedInput],
      originalInput: input,
    };
  }

  // Check pattern commands with context
  for (const { pattern, handler } of PATTERN_COMMANDS) {
    const match = trimmedInput.match(pattern);
    if (match) {
      const result =
        typeof handler === 'function' ? handler(match, context) : handler;

      if (result) {
        return {
          isQuickCommand: true,
          ...result,
          originalInput: input,
        };
      }
    }
  }

  return null;
}

/**
 * Get help text for quick commands
 * @returns {string} Help message
 */
export function getQuickCommandHelp() {
  return `
**Quick Commands Available:**

**Navigation:**
• \`home\` or \`projects\` - Go to projects list
• \`back\` or \`go back\` - Navigate back
• \`forward\` - Navigate forward
• \`go to project [name]\` - Open a specific project
• \`show [room type]\` - Navigate to a room (e.g., "show bathroom")

**Actions:**
• \`logout\` or \`sign out\` - Sign out of your account
• \`fullscreen\` - Toggle fullscreen mode
• \`clear chat\` - Clear chat history
• \`export pdf/ifc/dxf\` - Export current structure

**Help:**
• \`help\` or \`?\` - Show this help message
• \`commands\` - List available commands

These commands execute instantly without AI processing.
For complex requests, just type naturally and I'll help!
  `.trim();
}

/**
 * Get suggested quick commands based on context
 * @param {Object} context - Current context
 * @returns {Array} Suggested commands
 */
export function getSuggestedCommands(context = {}) {
  const suggestions = ['help', 'home', 'back'];

  if (context?.location?.structureId) {
    suggestions.push('export pdf', 'show bathroom', 'show kitchen');
  }

  if (context?.location?.projectId) {
    suggestions.push('show structures');
  }

  if (context.messages?.length > 0) {
    suggestions.push('clear chat');
  }

  return suggestions;
}
